import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { useTranslation } from 'react-i18next';
import type { ProfitabilityByProductRow } from '../../models/Profitability';
import {
  formatProfitabilityAmount,
  formatProfitabilityPercent,
  profitabilityNetMarginPercent,
} from '../../utils/formatMoney';

interface ProfitabilityProductBreakdownModalProps {
  row: ProfitabilityByProductRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locale: string;
  currency: string;
}

function BreakdownLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border py-2.5 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="shrink-0 tabular-nums text-foreground">{value}</span>
    </div>
  );
}

export const ProfitabilityProductBreakdownModal = ({
  row,
  open,
  onOpenChange,
  locale,
  currency,
}: ProfitabilityProductBreakdownModalProps) => {
  const { t } = useTranslation('profitabilityModule');

  if (!row) return null;

  const netMarginPct = profitabilityNetMarginPercent(row.allocated_revenue, row.profit);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="profitability-product-breakdown-modal">
        <DialogHeader>
          <DialogTitle>{t('by_product.breakdown.title')}</DialogTitle>
          <p className="truncate text-sm font-medium text-foreground">{row.product_name}</p>
          {row.code ? (
            <p className="text-xs text-muted-foreground tabular-nums">{row.code}</p>
          ) : null}
        </DialogHeader>

        <div className="rounded-lg border border-border bg-muted/30 px-3">
          <BreakdownLine
            label={t('by_product.row.revenue')}
            value={formatProfitabilityAmount(row.allocated_revenue, locale, currency)}
          />
          <BreakdownLine
            label={t('by_product.cogs')}
            value={formatProfitabilityAmount(row.line_cogs, locale, currency)}
          />
          <BreakdownLine
            label={t('by_product.logistics')}
            value={formatProfitabilityAmount(row.allocated_logistics, locale, currency)}
          />
          <BreakdownLine
            label={t('by_product.row.operating_profit')}
            value={formatProfitabilityAmount(row.operating_profit, locale, currency)}
          />
          <BreakdownLine
            label={t('by_product.row.ad_spend')}
            value={formatProfitabilityAmount(row.ad_spend, locale, currency)}
          />
          <BreakdownLine
            label={t('by_product.breakdown.net_with_margin')}
            value={
              netMarginPct != null
                ? `${formatProfitabilityAmount(row.profit, locale, currency)} (${formatProfitabilityPercent(netMarginPct, locale)})`
                : formatProfitabilityAmount(row.profit, locale, currency)
            }
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('by_product.breakdown.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
